/**
 * The lists the coordinator Overview links to, one entry per `show` value a
 * link may carry in the URL (`?tab=...&show=...`). Each entry filters the
 * tab's rows by the same field the server counted for the Overview
 * (programOutcome on a request, status on a TA request, attention on a
 * position), so the list a link opens is as long as the number it showed.
 * `phrase` finishes the sentence "Showing the 3 ..." above the list.
 */

const plural = (count, one, many) => (count === 1 ? one : many);
const requests = (count) => plural(count, 'request', 'requests');
const sections = (count) => plural(count, 'section', 'sections');

export const ALL = 'all';

// Not a filter of the request list: the `show` value that opens the "Started
// but not submitted" section below it
export const DRAFTS_SHOW = 'drafts';

// A course support request for SI or LA, by where it stands for the program
const requestLists = (programType) => {
    // SI and LA both take "an": "an SI decision", "an LA decision"
    const program = programType.toUpperCase();
    return {
        approved: {
            matches: (app) => app.programOutcome === 'approved',
            phrase: (n) => `${requests(n)} the ${program} program approved`,
        },
        provisional: {
            matches: (app) => app.programOutcome === 'provisional',
            phrase: (n) =>
                `${requests(n)} with a contingent ${program} award that is not final yet`,
        },
        waitlisted: {
            matches: (app) => app.programOutcome === 'waitlisted',
            phrase: (n) => `${requests(n)} the ${program} program waitlisted`,
        },
        not_selected: {
            matches: (app) => app.programOutcome === 'not_selected',
            phrase: (n) =>
                `${requests(n)} the ${program} program did not select`,
        },
        waiting: {
            matches: (app) => app.programOutcome === 'waiting',
            phrase: (n) => `${requests(n)} waiting for an ${program} decision`,
        },
        revision: {
            matches: (app) => app.programOutcome === 'revision',
            phrase: (n) => `${requests(n)} out for revision with faculty`,
        },
        elsewhere: {
            matches: (app) => app.programOutcome === 'elsewhere',
            phrase: (n) => `${requests(n)} another program is supporting`,
        },
        withdrawn: {
            matches: (app) => app.programOutcome === 'withdrawn',
            phrase: (n) => `withdrawn ${requests(n)}`,
        },
        [ALL]: { matches: () => true, phrase: (n) => requests(n) },
    };
};

export const APPLICATION_LISTS = {
    si: requestLists('si'),
    la: requestLists('la'),
};

const taStatus = (status, phrase) => ({
    matches: (request) => request.status === status,
    phrase,
});

export const TA_REQUEST_LISTS = {
    pending: taStatus(
        'pending',
        (n) => `${requests(n)} waiting for a decision`
    ),
    approved: taStatus('approved', (n) => `approved ${requests(n)}`),
    denied: taStatus('denied', (n) => `declined ${requests(n)}`),
    cancelled: taStatus('cancelled', (n) => `cancelled ${requests(n)}`),
    converted: taStatus('converted', (n) => `converted ${requests(n)}`),
    [ALL]: { matches: () => true, phrase: (n) => requests(n) },
};

export const POSITION_LISTS = {
    offers_expired: {
        matches: (p) => p.attention?.offersExpired > 0,
        phrase: (n) =>
            `${sections(n)} with an offer that expired without an answer`,
    },
    offers_expiring: {
        matches: (p) => p.attention?.offersExpiringSoon > 0,
        phrase: (n) =>
            `${sections(n)} with an offer that expires in the next 3 days`,
    },
    review_waiting: {
        matches: (p) => p.attention?.waitingForReview > 0,
        phrase: (n) =>
            `${sections(n)} with students waiting 7 days or more for review`,
    },
    no_applicants: {
        matches: (p) => p.attention?.needsApplicants === true,
        phrase: (n) =>
            `${sections(n)} with places available and no applicants after two weeks`,
    },
    [ALL]: { matches: () => true, phrase: (n) => sections(n) },
};

/** The list entry a `show` value names, or null for none or an unknown one. */
export const listFor = (lists, show) =>
    show && Object.hasOwn(lists, show) ? lists[show] : null;

/** The URL search a link uses to open a tab with a list shown. */
export const listSearch = (tab, show = ALL) =>
    `?${new URLSearchParams({ tab, show }).toString()}`;
